import React from 'react';

import AppModal from '../../../../ui-components/AppModal';
import StatusTag from '../../../../ui-components/StatusTag';
import { SecondaryButton } from '../../../../ui-components/listing';
import { Skeleton } from '../../../../ui-components/skeleton';
import UserAvatar from '../../../../ui-components/UserAvatar';
import { API_CONSTANTS } from '../../../../utils/constants';
import {
  formatTimestampToDate,
  formatTimestampToDateTime,
} from '../../../../utils/common';

interface AdminDetailsModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  name?: string;
  email?: string;
  roleLabel?: string;
  dialCode?: string;
  phoneNumber?: string;
  status?: string;
  createdDateLabel?: string;
  lastLoginLabel?: string;
  avatarUrl?: string;
  username?: string;
  designationName?: string;
  departementName?: string;
  subDepartementName?: string;
  joiningDate?: string | number;
  immediateSupervisorID?: number;
  immediateSupervisorName?: string;
  lineManagerID?: number;
  lineManagerName?: string;
  hodId?: number;
  hodName?: string;
  siteName?: string;
  buildingName?: string;
  floorName?: string;
  onClose: () => void;
}

const calculateExperience = (joiningDate?: string | number): string | null => {
  if (!joiningDate) return null;

  const parsed = new Date(joiningDate);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  let totalMonths =
    (today.getFullYear() - parsed.getFullYear()) * 12 +
    (today.getMonth() - parsed.getMonth());

  if (today.getDate() < parsed.getDate()) {
    totalMonths -= 1;
  }

  if (totalMonths < 0) totalMonths = 0;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return `${years} years, ${months} months`;
};

const getProfileImageUrl = (id?: number): string | undefined => {
  if (!id || id <= 0) return undefined;

  try {
    return new URL(
      `public/upload/profile_image/thumbs/${id}.jpg`,
      API_CONSTANTS.BASE_URL
    ).toString();
  } catch {
    return undefined;
  }
};

const formatJoiningDate = (value?: string | number): string => {
  return value ? formatTimestampToDateTime(value) : '';
};

const AdminDetailsModal: React.FC<AdminDetailsModalProps> = ({
  isOpen,
  isLoading = false,
  name,
  email,
  roleLabel,
  dialCode,
  phoneNumber,
  status,
  createdDateLabel,
  lastLoginLabel,
  avatarUrl,
  username,
  designationName,
  departementName,
  subDepartementName,
  joiningDate,
  immediateSupervisorID,
  immediateSupervisorName,
  lineManagerID,
  lineManagerName,
  hodId,
  hodName,
  siteName,
  buildingName,
  floorName,
  onClose,
}) => {
  const departmentValue = departementName?.trim() ?? '';
  const subDepartmentValue = subDepartementName?.trim() ?? '';
  const joiningDateLabel = formatJoiningDate(joiningDate);
  const experienceLabel = calculateExperience(joiningDate);

  const hierarchy = [
    {
      key: 'supervisor',
      label: 'Supervisor',
      name: immediateSupervisorName,
      id: immediateSupervisorID,
    },
    {
      key: 'lineManager',
      label: 'Line Manager',
      name: lineManagerName,
      id: lineManagerID,
    },
    {
      key: 'hod',
      label: 'HOD',
      name: hodName,
      id: hodId,
    },
  ].filter((person) => Boolean(person.name?.trim()));

  const displayUsername = username?.trim();
  const displayDesignation = designationName?.trim();
  const showDepartmentRow = Boolean(departmentValue || subDepartmentValue);
  const showUserDesignationRow = Boolean(displayUsername || displayDesignation);
  const displayName = name?.trim() || 'User';
  const descriptionName = name?.trim() ? name.trim().toLowerCase() : 'admin';

  return (
    <AppModal
      isOpen={isOpen}
      title={`${displayName}'s Details`}
      description={`View complete ${descriptionName} information and account status.`}
      onClose={onClose}
      hideFooter
      variant="form"
      maxWidth="max-w-2xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center gap-3 pb-6 border-b border-neutral-200 dark:border-neutral-800">
            <Skeleton variant="circle" className="w-20 h-20" />
            <div className="flex flex-col items-center gap-2 w-full">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`detail-skeleton-${index}`} className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col items-center text-center pb-6 border-b border-neutral-200 dark:border-neutral-800">
            <UserAvatar src={avatarUrl} alt={name || 'Admin'} size="xl" />
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mt-3">
              {name || '-'}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {roleLabel || '-'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Roles
              </label>
              <p className="text-sm text-neutral-900 dark:text-white break-words">
                {roleLabel || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Status
              </label>
              <StatusTag value={status} />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Email
              </label>
              <p className="text-sm text-neutral-900 dark:text-white break-words">
                {email || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Phone Number
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {phoneNumber ? `${dialCode ?? ''}${phoneNumber}` : '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Created Date
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {createdDateLabel || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Last Login
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {lastLoginLabel || 'Never'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Site
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {siteName || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Building
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {buildingName || '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                Floor
              </label>
              <p className="text-sm text-neutral-900 dark:text-white">
                {floorName || '-'}
              </p>
            </div>
          </div>

          {showUserDesignationRow ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Username
                </label>
                <p className="text-sm text-neutral-900 dark:text-white">
                  {displayUsername || '-'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                  Designation
                </label>
                {displayDesignation ? (
                  <span className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-700 px-3 py-1 text-xs font-semibold dark:bg-neutral-800 dark:text-neutral-200">
                    {displayDesignation}
                  </span>
                ) : (
                  <p className="text-sm text-neutral-900 dark:text-white">-</p>
                )}
              </div>
            </div>
          ) : null}

          {showDepartmentRow ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Department
                </label>
                <p className="text-sm text-neutral-900 dark:text-white">
                  {departmentValue || '-'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Sub Department
                </label>
                <p className="text-sm text-neutral-900 dark:text-white">
                  {subDepartmentValue || '-'}
                </p>
              </div>
            </div>
          ) : null}

          {hierarchy.length ? (
            <div className="space-y-3">
              <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Hierarchy
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-flow-col sm:auto-cols-fr">
                {hierarchy.map((person) => (
                  <div
                    key={person.key}
                    className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/60"
                  >
                    <UserAvatar
                      src={getProfileImageUrl(person.id)}
                      alt={person.name || person.label}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                        {person.name}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {person.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {joiningDateLabel && experienceLabel ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Joining Date
                </label>
                <p className="text-sm text-neutral-900 dark:text-white">
                  {formatTimestampToDate(joiningDateLabel)}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Experience
                </label>
                <p className="text-sm text-neutral-900 dark:text-white">
                  {experienceLabel}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-4 border-t border-neutral-200 dark:border-neutral-800">
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </div>
    </AppModal>
  );
};

export default AdminDetailsModal;
